import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// API's
import { getUsersByPositionInputPayPlanAssignment } from "@/PayPlans/api/assignments/queries";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Layouts
import PayPlanAssignmentsLoader from "@/Layouts/components/Skeletons/PayPlanAssignmentsLoader";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";

// Components
import PayPlanInformationCard from "./PayPlanInformation/PayPlanInformationCard.jsx";

// Materials
import { Divider, Grid, Paper, Typography } from "@mui/material";
import { retrievePositionsMap } from "@/Common/api/positions/queries";
//Subscriptions
import { getAssignmentsSubscriptions } from "./subscriptions";

export default function CurrentBody(props) {
	const { positions, xsInformation } = props;
	const [wColumnInformation, setWColumnInformation] = useState(4);
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;
	const queryClient = useQueryClient();
	const [updateData, setUpdateData] = useState(false);

	/******************************************** SUBSCRIPTIONS ***************************************************/
	const { getSubscription, addSubscription, clearSubscriptionContext } = useSubscriptions();
	const payPlansSubscription = getSubscription(["currentPayPlanAssignmentUsersByPosition", positions, locationId]);
	payPlansSubscription?.subscriptions?.onCreatePayPlanAssignment?.setCallback((_response) => {
		queryClient.setQueryData(["usersByPositionCurrentPayPlan", locationId, positions], (currentCache) => {
			if (currentCache) {
				const usersDataResponse = _response.data.onCreatePayPlanAssignment?.users;
				currentCache.map((userCached) => {
					if (positions.includes(userCached.position)) {
						usersDataResponse?.forEach((userResponse) => {
							if (userResponse._id === userCached._id) {
								userCached.upcomingPayPlanAssignment = userResponse?.upcomingPayPlanAssignment;
								setUpdateData(!updateData);
							}
						});
					}
				});
				return currentCache;
			}
			return currentCache;
		});
	});

	payPlansSubscription?.subscriptions?.onUpdateUserPayPlanAssignment?.setCallback((_response) => {
		const userId = _response.data?.onUpdateUserPayPlanAssignment?._id;
		queryClient.setQueryData(["usersByPositionCurrentPayPlan", locationId, props.positions], (currentCache) => {
			if (currentCache?.length) {
				const user = currentCache.find((element) => element._id === userId);
				if (user) {
					user.upcomingPayPlanAssignment = _response.data?.onUpdateUserPayPlanAssignment?.upcomingPayPlanAssignment;
					setUpdateData(!updateData);
					return currentCache;
				} else {
					return currentCache;
				}
			}
			return currentCache;
		});
	});

	payPlansSubscription?.subscriptions?.onRemoveUserPayPlanAssignment?.setCallback((_response) => {
		const userId = _response.data?.onRemoveUserPayPlanAssignment?._id;
		queryClient.setQueryData(["usersByPositionCurrentPayPlan", locationId, props.positions], (currentCache) => {
			if (currentCache?.length) {
				const user = currentCache.find((element) => element._id === userId);
				if (user) {
					delete user.upcomingPayPlanAssignment;
					setUpdateData(!updateData);
					return currentCache;
				} else {
					return currentCache;
				}
			}
			return currentCache;
		});
	});

	// ******************************************** QUERIES ***************************************************
	const { data: typePositionsSettings, isLoading: isLoadingPositionTypes } = useQuery({
		queryKey: ["typePositionsSettings", location?.locationId],
		queryFn: async () => retrievePositionsMap(location?.locationId),
		enabled: !!location?.locationId,
		staleTime: Infinity,
	});

	const { data, isLoading } = useQuery({
		queryKey: ["usersByPositionCurrentPayPlan", locationId, positions],
		queryFn: async () => {
			const positionsList = [];
			const positionSelected = positions.length >= 1 ? positions[0] : "";
			typePositionsSettings.find((item) => {
				positionSelected === item.systemPosition &&
					positionsList.push({ position: positionSelected, isDepartmental: item.isDepartmental });
			});
			return getUsersByPositionInputPayPlanAssignment(locationId, positionsList);
		},
		staleTime: Infinity,
		enabled: !!positions?.length && !!locationId && !!typePositionsSettings?.length,
	});

	useEffect(() => {
		if (!location || !positions?.length) {
			return;
		}
		clearSubscriptionContext();
		if (xsInformation) {
			setWColumnInformation(xsInformation);
		}
	}, [location, positions, xsInformation]);

	useEffect(() => {
		addSubscription(["currentPayPlanAssignmentUsersByPosition", positions, locationId], getAssignmentsSubscriptions());
	}, [addSubscription, positions, locationId]);

	const validatePendingAcknowledgement = (user) => {
		const upcomingPayPlanAssignment = user?.upcomingPayPlanAssignment;
		if (!upcomingPayPlanAssignment) {
			return false;
		}
		const acknowledgementStatus = upcomingPayPlanAssignment?.acknowledgementStatus || [];
		const lastStatus = acknowledgementStatus?.[acknowledgementStatus?.length - 1]?.status;
		user.upcomingPayPlanAssignment.lastStatus = lastStatus;
		return lastStatus === "INPROGRESS" || lastStatus === "DRAFT";
	};

	const dataPendingAcknowledgement = data?.filter((user) => validatePendingAcknowledgement(user));
	const dataAcknowledgement = data?.filter((user) => !validatePendingAcknowledgement(user));

	return (
		<RoundedBox sx={{ minHeight: "630px" }}>
			<Grid container spacing={1} position="relative">
				<Grid item xs={12}>
					{isLoading || isLoadingPositionTypes ? (
						<PayPlanAssignmentsLoader positionTitle />
					) : (
						Boolean(data?.length) && (
							<Grid columnSpacing={4} rowSpacing={4}>
								{["Pending Acknowledgement", "Acknowledged"].map((item, index) => (
									<div key={"Key" + index}>
										<Typography
											className="select-plan-dialog-current-assign-title"
											sx={{
												paddingBottom: "32px",
											}}
										>
											{item}
										</Typography>
										<Paper elevation={0} className="section">
											<Grid
												container
												columnSpacing={3}
												rowSpacing={3}
												sx={{
													paddingBottom: "20px",
												}}
											>
												{item === "Pending Acknowledgement"
													? dataPendingAcknowledgement?.map((user) => {
															return (
																<Grid item xs={4} key={user._id}>
																	<PayPlanInformationCard {...user} sectionType={item} />
																</Grid>
															);
													  })
													: dataAcknowledgement?.map((user) => (
															<Grid item xs={wColumnInformation} key={user._id}>
																<PayPlanInformationCard {...user} sectionType={item} />
															</Grid>
													  ))}
											</Grid>
										</Paper>
										<Divider className="w-full !mt-[32px] !mb-[32px]" />
									</div>
								))}
							</Grid>
						)
					)}
				</Grid>
			</Grid>
		</RoundedBox>
	);
}

CurrentBody.propTypes = {
	positions: PropTypes.array,
	department: PropTypes.string,
	xsInformation: PropTypes.number,
};
